/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { Area } from "react-easy-crop/types"
import { EventCategory, EventAction } from "constants/tracking"
import { TroyWebTracker } from "utils/tracking"

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener("load", () => resolve(image))
    image.addEventListener("error", error => reject(error))
    image.setAttribute("crossOrigin", "anonymous") // needed to avoid cross-origin issues on CodeSandbox
    image.src = url
  })

function getRadianAngle(degreeValue: number) {
  return (degreeValue * Math.PI) / 180
}

export const getFileMimeType = (
  file: Blob,
): Promise<ReturnType<typeof getHeaderMimeType>> => {
  return new Promise((resolve, reject) => {
    // must use `window.FileReader` instead of just `FileReader`, else test will throw error
    const fileReader = new window.FileReader()
    fileReader.onloadend = (e: ProgressEvent<FileReader>) => {
      if (
        !e.target?.result ||
        !(e.target.result instanceof ArrayBuffer) ||
        e.target.result.byteLength < 4
      ) {
        reject(new Error("invalid file"))
        return
      }

      const arr = new Uint8Array(e.target.result).subarray(0, 4)
      let header = ""
      for (let i = 0; i < arr.length; i += 1) {
        header += arr[i].toString(16)
      }
      resolve(getHeaderMimeType(header))
    }
    fileReader.onerror = () => {
      reject(fileReader.error)
    }
    fileReader.readAsArrayBuffer(file)
  })
}

export const getImageMimeType = async (
  imageSrc: string,
): Promise<ReturnType<typeof getHeaderMimeType>> => {
  const res = await fetch(imageSrc)
  const blob = await res.blob()
  return getFileMimeType(blob)
}

const imageDataHasTransparency = (imageData: ImageData) => {
  const data = imageData.data
  // because RGBA order, alpha is the fourth element
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) {
      return true
    }
  }
  return false
}

const imageDataToCanvas = (imageData: ImageData) => {
  const canvas = document.createElement("canvas")
  const ctx = canvas.getContext("2d")!
  canvas.width = imageData.width
  canvas.height = imageData.height
  ctx.putImageData(imageData, 0, 0)
  return canvas
}

const getImageData = (image: HTMLImageElement) => {
  const canvas = document.createElement("canvas")
  const ctx = canvas.getContext("2d")!
  canvas.width = image.width
  canvas.height = image.height
  ctx.drawImage(image, 0, 0)
  return ctx.getImageData(0, 0, image.width, image.height)
}

type ImageDataWithOrigSize = {
  data: ImageData
  contentWidth: number
  contentHeight: number
}

const rotateImageDataWithSafeSize = (
  imageData: ImageDataWithOrigSize,
  rotation = 0,
): ImageDataWithOrigSize => {
  const newRotation = rotation % 360
  if (newRotation === 0) {
    return imageData
  }

  const canvas = document.createElement("canvas")
  const ctx = canvas.getContext("2d")!
  const maxSize = Math.max(imageData.data.width, imageData.data.height)

  let safeArea = Math.ceil(2 * ((maxSize / 2) * Math.sqrt(2)))
  if (safeArea % 2 === 1) {
    safeArea += 1
  }
  // safeArea must be divisible by 2, else the resulting image will have ghost lines at the left and top
  const halfSafeArea = safeArea / 2

  // set each dimensions to double largest dimension to allow for a safe area for the
  // image to rotate in without being clipped by canvas context
  canvas.width = safeArea
  canvas.height = safeArea

  // translate canvas context to a central location on image to allow rotating around the center.
  ctx.translate(halfSafeArea, halfSafeArea)
  ctx.rotate(getRadianAngle(newRotation))
  ctx.translate(-halfSafeArea, -halfSafeArea)

  // draw rotated image
  // NOTE: you cannot use `putImageData` here because it'll be unaffected by the rotation, hence a temporary canvas is needed to use `drawImage`
  const innerCanvas = imageDataToCanvas(imageData.data)
  ctx.drawImage(
    innerCanvas,
    halfSafeArea - innerCanvas.width / 2,
    halfSafeArea - innerCanvas.height / 2,
  )

  return {
    data: ctx.getImageData(0, 0, canvas.width, canvas.height),
    contentWidth: innerCanvas.width,
    contentHeight: innerCanvas.height,
  }
}

const cropImageData = (
  imageData: ImageDataWithOrigSize,
  pixelCrop: Area,
): ImageDataWithOrigSize => {
  const canvas = document.createElement("canvas")
  // set canvas width to final desired crop size - this will reset the canvas
  canvas.width = pixelCrop.width
  canvas.height = pixelCrop.height

  // get a new context, avoid reusing
  const ctx = canvas.getContext("2d")!

  // paste generated rotate image with correct offsets for x,y crop values.
  ctx.putImageData(
    imageData.data,
    Math.round(
      0 - imageData.data.width / 2 + imageData.contentWidth / 2 - pixelCrop.x,
    ),
    Math.round(
      0 - imageData.data.height / 2 + imageData.contentHeight / 2 - pixelCrop.y,
    ),
  )

  return {
    data: ctx.getImageData(0, 0, pixelCrop.width, pixelCrop.height),
    contentWidth: pixelCrop.width,
    contentHeight: pixelCrop.height,
  }
}

export async function getTransformedImageDataURI(
  imageSrc: string, // file path
  pixelCrop?: Area,
  rotation = 0,
) {
  const origImageData = getImageData(await createImage(imageSrc))
  const origMimeType = await getImageMimeType(imageSrc)

  let currentImageData: ImageDataWithOrigSize = {
    data: origImageData,
    contentWidth: origImageData.width,
    contentHeight: origImageData.height,
  }

  currentImageData = rotateImageDataWithSafeSize(currentImageData, rotation)
  let currentCropInfo = pixelCrop
  if (!currentCropInfo) {
    // Have a default crop value, because rotate will produce a image larger than the input image
    currentCropInfo = {
      width: origImageData.width,
      height: origImageData.height,
      x: 0,
      y: 0,
    }
  }
  currentImageData = cropImageData(currentImageData, currentCropInfo)

  // Note that if we don't specify `image/png` for png files, the transparency will be lost
  // Converting `image/jpeg` to `image/png` will make the output image file size much larger
  let newMimeType = origMimeType === "image/png" ? "image/png" : "image/jpeg"

  if (newMimeType === "image/png" && !imageDataHasTransparency(origImageData)) {
    // Convert png to jpeg if there's no transparency used in original image, because jpeg will be smaller
    newMimeType = "image/jpeg"
  }

  // new canvas to host the image data
  const canvas = document.createElement("canvas")
  const ctx = canvas.getContext("2d")!
  canvas.width = currentImageData.data.width
  canvas.height = currentImageData.data.height
  ctx.putImageData(currentImageData.data, 0, 0)

  // As Base64 string
  return getDataURIWithValidImageSize(canvas, newMimeType)
}

export function readFileAsDataURL(file: Blob): Promise<string> {
  return new Promise(resolve => {
    // must use `window.FileReader` instead of just `FileReader`, else test will throw error
    const reader = new window.FileReader()
    reader.addEventListener(
      "load",
      () => {
        const result = reader?.result
        if (typeof result === "string") {
          resolve(result)
        } else {
          resolve("")
        }
      },
      false,
    )
    reader.readAsDataURL(file)
  })
}

export function getBase64String(data: string) {
  // Use a regex to remove data url part
  const base64String = data.replace("data:", "").replace(/^.+,/, "")

  return base64String
}

export function getFileType(file: File) {
  return file.type.replace(/(.*)\//g, "")
}

export function isTmpUrl(imageUrl: string) {
  return imageUrl.search("blob:") === 0
}

/**
 * Converts an image URL to base64 data URL.
 * If the input is not a URL (already base64 or data URL), returns as-is.
 */
export const convertUrlToBase64DataUrl = async (
  url: string,
): Promise<string> => {
  if (!/^https?:\/\//i.test(url)) {
    return url
  }
  const response = await fetch(url, { cache: "no-cache" })
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status}`)
  }
  const blob = await response.blob()
  return readFileAsDataURL(blob)
}

/**
 * detect and return the file's file type through the file's signature
 * https://en.wikipedia.org/wiki/List_of_file_signatures
 * @returns the file type
 */
const getHeaderMimeType = (
  header: string,
): "image/jpeg" | "image/png" | "image/gif" | "unknown" => {
  // most jpegs start with this file signature https://github.com/neilharvey/FileSignatures/issues/1#issuecomment-276366061
  // reference impl https://github.com/neilharvey/FileSignatures/blob/master/src/FileSignatures/Formats/Jpeg.cs
  const jpegFileSignaturePrefix = "ffd8"
  if (header.startsWith(jpegFileSignaturePrefix)) {
    return "image/jpeg"
  }
  switch (header) {
    case "89504e47":
      return "image/png"
    case "47494638":
      return "image/gif"
    default:
      return "unknown" // Or you can use the blob.type as fallback
  }
}

export const isFileValidImageSize = (file: Blob) => {
  // Note: There's discrepancy in the limits, MEX app and bulk upload only supports 2mb
  // Lesser than 2mb
  return file.size / 1024 / 1024 <= 2
}

/** If png has transparency, we need to replace the background as white  */
const replaceTransparencyWithWhiteBackground = (canvas: HTMLCanvasElement) => {
  const ctx = canvas.getContext("2d")!

  if (
    !imageDataHasTransparency(
      ctx.getImageData(0, 0, canvas.width, canvas.height),
    )
  ) {
    return canvas
  }

  // set to draw behind current content
  ctx.globalCompositeOperation = "destination-over"

  ctx.fillStyle = "#fff"
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  return canvas
}

const getDataURIWithValidImageSize = async (
  canvas: HTMLCanvasElement,
  mimeType: string,
  currentQuality: number = 1.0,
): Promise<string> => {
  const quality_step = 0.05
  const max_worst_quality = 0.85
  const max_dimension = 2000 // Maximum width or height

  // Check if canvas dimensions exceed maximum and resize if needed
  const currentMaxDimension = Math.max(canvas.width, canvas.height)
  let workingCanvas = canvas

  if (currentMaxDimension > max_dimension) {
    const scaleFactor = max_dimension / currentMaxDimension
    const newWidth = Math.floor(canvas.width * scaleFactor)
    const newHeight = Math.floor(canvas.height * scaleFactor)

    // Create resized canvas
    const resizedCanvas = document.createElement("canvas")
    const ctx = resizedCanvas.getContext("2d")!

    resizedCanvas.width = newWidth
    resizedCanvas.height = newHeight

    // Use high-quality scaling
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = "high"

    ctx.drawImage(canvas, 0, 0, newWidth, newHeight)

    // Use the resized canvas for compression
    workingCanvas = resizedCanvas
  }

  const resultBlob = await new Promise<Blob | undefined>(resolve => {
    workingCanvas.toBlob(
      blob => {
        if (blob && isFileValidImageSize(blob)) {
          resolve(blob)
          return
        }
        resolve(undefined)
      },
      mimeType,
      currentQuality,
    )
  })

  if (resultBlob) {
    return readFileAsDataURL(resultBlob)
  }

  // only jpeg and webp supports changing quality, but we only support jpeg upload in mex portal
  if (mimeType !== "image/jpeg") {
    return getDataURIWithValidImageSize(
      replaceTransparencyWithWhiteBackground(workingCanvas),
      "image/jpeg",
      currentQuality,
    )
  }

  const nextQuality = currentQuality - quality_step

  if (nextQuality < max_worst_quality) {
    // Track compression failure for monitoring
    TroyWebTracker.sendEvent({
      category: EventCategory.menuItemsTab,
      action: EventAction.imageCompressionFailed,
      labels: {
        WIDTH: workingCanvas.width,
        HEIGHT: workingCanvas.height,
        TOTAL_PIXELS: workingCanvas.width * workingCanvas.height,
        MIME_TYPE: mimeType,
        FINAL_QUALITY: nextQuality,
        MAX_WORST_QUALITY: max_worst_quality,
      },
    })

    throw new Error(
      `Unable to produce valid image size (${mimeType}) even at lowest quality (${nextQuality})`,
    )
  }

  return getDataURIWithValidImageSize(workingCanvas, mimeType, nextQuality)
}
